import {
  FileFormat, ReportDocument, ResultSnapshot, TableData,
} from './runtime/files';
import type { ExperienceCandidate } from './runtime/experience';
import type { TaskSpec } from './runtime/task';
import type { QueuedJob } from './runtime/queued';

const DB_NAME = 'elman-agent';
const DB_VERSION = 1;
const STORE = 'threads';

export type StoredMessage = {
  role: 'user' | 'assistant';
  text: string;
  table?: TableData;
  formats?: FileFormat[];
  result?: ResultSnapshot;
  report?: ReportDocument;
  /** v2의 구조화 업무 상태. 짧은 후속 발화가 원래 목표를 잃지 않게 한다. */
  task?: TaskSpec;
  /** 그 답변을 만들 때 조회한 화면들. 이어지는 질문의 후보에 되돌려 놓는 데 쓴다. */
  routes?: string[];
  /** 검증된 실행 레시피 후보. 업무 행 값은 포함하지 않는다. */
  experience?: ExperienceCandidate;
  feedback?: 'positive' | 'negative';
  /** 이 턴이 서버에 맡긴 일. 패널이 끝을 지켜본다. */
  queued?: QueuedJob[];
  /** 맡긴 일이 끝났다는 알림. 「결과 받기」 단추가 붙는다. */
  finished?: QueuedJob;
};

export type Thread = {
  id: string;
  title: string;
  updatedAt: number;
  messages: StoredMessage[];
};

/**
 * **스토어가 없으면 만들어서 연다.**
 *
 * `onupgradeneeded` 는 **버전이 올라갈 때만** 불린다. 그래서 이 브라우저에
 * `elman-agent` 가 이미 버전 1로 있는데 `threads` 스토어가 없으면 — 예전 빌드가
 * 다른 이름으로 만들었거나 업그레이드가 중간에 끊겼으면 — 고정 버전으로 여는 한
 * **영영 안 만들어진다.** 모든 읽기·쓰기가 이렇게 죽는다:
 *
 * ```
 * NotFoundError: Failed to execute 'transaction' on 'IDBDatabase':
 * One of the specified object stores was not found.
 * ```
 *
 * 실제로 개발 서버 콘솔에 계속 찍히고 있었다(2026-09-21). 그 브라우저에서는 대화가
 * 하나도 저장되지 않는다.
 *
 * 그래서 **지금 버전으로 열어 보고**, 스토어가 없으면 그때 버전을 하나 올려 다시 연다.
 * 버전을 코드에 박아 두면 이 회복을 못 한다.
 */
function open(): Promise<IDBDatabase> {
  const make = (db: IDBDatabase): void => {
    if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
  };
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => make(request.result);
    request.onsuccess = () => {
      const db = request.result;
      if (db.objectStoreNames.contains(STORE)) { resolve(db); return; }
      // 스토어가 없다. 버전을 올려야 `onupgradeneeded` 가 돈다.
      const next = db.version + 1;
      db.close();
      const again = indexedDB.open(DB_NAME, next);
      again.onupgradeneeded = () => make(again.result);
      again.onsuccess = () => resolve(again.result);
      again.onerror = () => reject(again.error);
    };
    request.onerror = () => reject(request.error);
  });
}

function keyOf(owner: string, id: string): string {
  return `${owner}:${id}`;
}

/** 대화가 아니라 사용자에게 붙는 것이라 대화 열쇠와 섞이지 않게 접두어를 다르게 둔다. */
function aliasKey(owner: string): string {
  return `aliases!${owner}`;
}

export async function listThreads(owner: string): Promise<Thread[]> {
  const db = await open();
  return new Promise((resolve, reject) => {
    const request = db.transaction(STORE, 'readonly').objectStore(STORE).openCursor();
    const found: Thread[] = [];
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) {
        resolve(found.sort((a, b) => b.updatedAt - a.updatedAt));
        return;
      }
      if (String(cursor.key).startsWith(`${owner}:`)) found.push(cursor.value as Thread);
      cursor.continue();
    };
    request.onerror = () => reject(request.error);
  });
}

export async function saveThread(owner: string, thread: Thread): Promise<void> {
  const db = await open();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE, 'readwrite');
    transaction.objectStore(STORE).put(thread, keyOf(owner, thread.id));
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

/**
 * 사람이 말한 이름 → 화면이 확정한 이름.
 *
 * 한 대화 안에서만 기억하면 새 대화를 열 때마다 같은 현장을 다시 고르게 된다. 같은
 * 곳을 매일 쓰는 사람에게는 그게 곧 "안 쓰는 기능" 이다. 대화 목록과 같은 저장소에
 * 사용자별로 한 덩이만 둔다 — 행 값이 아니라 이름 두 개짜리 짝이라 가볍다.
 */
export async function loadAliases(owner: string): Promise<Record<string, string>> {
  const db = await open();
  return new Promise((resolve, reject) => {
    const request = db.transaction(STORE, 'readonly').objectStore(STORE).get(aliasKey(owner));
    request.onsuccess = () => resolve((request.result as Record<string, string>) ?? {});
    request.onerror = () => reject(request.error);
  });
}

export async function saveAliases(owner: string, aliases: Record<string, string>): Promise<void> {
  const db = await open();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE, 'readwrite');
    transaction.objectStore(STORE).put(aliases, aliasKey(owner));
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

export async function removeThread(owner: string, id: string): Promise<void> {
  const db = await open();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE, 'readwrite');
    transaction.objectStore(STORE).delete(keyOf(owner, id));
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}
